import { useMemo } from 'react';
import { Lock } from 'lucide-react';
import { buildThemeVars, buildBackgroundStyle, getLogoHeight } from '@checkout/useCheckoutTheme';
import type { MerchantBranding, FloatingAsset } from '@shared/types';

interface CheckoutPreviewProps {
  branding: MerchantBranding;
  merchantName: string;
}

const SPEED_MAP: Record<string, string> = { slow: '8s', medium: '5s', fast: '3s' };

const PREVIEW_POSITIONS = [
  { left: 8, top: 6 },
  { left: 72, top: 12 },
  { left: 20, top: 55 },
  { left: 80, top: 60 },
  { left: 45, top: 82 },
];

function buildPreviewInstances(assets: FloatingAsset[]) {
  const instances: { asset: FloatingAsset; left: number; top: number; delay: number; idx: number }[] = [];
  let idx = 0;
  for (const asset of assets) {
    const count = Math.min(asset.count, 6);
    for (let i = 0; i < count; i++) {
      const pos = PREVIEW_POSITIONS[(idx) % PREVIEW_POSITIONS.length];
      instances.push({
        asset,
        left: pos.left + (i * 7) % 15,
        top: pos.top + (i * 11) % 20,
        delay: i * 0.8,
        idx: idx++,
      });
    }
  }
  return instances;
}

export default function CheckoutPreview({ branding, merchantName }: CheckoutPreviewProps) {
  const themeVars = buildThemeVars(branding);
  const bgStyle = buildBackgroundStyle(branding);
  const centered = branding.logo_position === 'center';
  const logoHeight = getLogoHeight(branding);
  const isDark = branding.checkout_theme !== 'light';
  const hasImageBg = branding.background_type === 'image' && branding.background_image_url;
  const hasFloatingAssets = branding.floating_assets_enabled && branding.floating_assets && branding.floating_assets.length > 0;

  const assetInstances = useMemo(
    () => hasFloatingAssets ? buildPreviewInstances(branding.floating_assets!) : [],
    [hasFloatingAssets, branding.floating_assets]
  );

  const fonts: string[] = [];
  if (branding.heading_font && branding.heading_font !== 'IBM Plex Sans') fonts.push(branding.heading_font);
  if (branding.body_font && branding.body_font !== 'IBM Plex Sans' && branding.body_font !== branding.heading_font) fonts.push(branding.body_font);

  return (
    <div className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
      {fonts.length > 0 && (
        <link
          rel="stylesheet"
          href={`https://fonts.googleapis.com/css2?${fonts.map(f => `family=${f.replace(/\s/g, '+')}:wght@400;500;600;700`).join('&')}&display=swap`}
        />
      )}

      {hasFloatingAssets && (
        <style>{`
          @keyframes bp-pv-bounce {
            0%, 100% { transform: translateY(0) rotate(0deg); }
            25% { transform: translateY(-18px) rotate(5deg); }
            50% { transform: translateY(-8px) rotate(-3deg); }
            75% { transform: translateY(-14px) rotate(2deg); }
          }
          @keyframes bp-pv-float {
            0%, 100% { transform: translateY(0) translateX(0); }
            33% { transform: translateY(-12px) translateX(6px); }
            66% { transform: translateY(-6px) translateX(-6px); }
          }
          @keyframes bp-pv-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes bp-pv-drift {
            0%, 100% { transform: translateX(0) translateY(0); }
            25% { transform: translateX(12px) translateY(-10px); }
            50% { transform: translateX(-6px) translateY(-16px); }
            75% { transform: translateX(10px) translateY(-6px); }
          }
        `}</style>
      )}

      <div className="flex items-center gap-1.5 px-3 py-2.5" style={{ background: 'var(--dash-input)', borderBottom: '1px solid var(--dash-border-light)' }}>
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--dash-hover-bg)' }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--dash-hover-bg)' }} />
        <span className="w-2.5 h-2.5 rounded-full" style={{ background: 'var(--dash-hover-bg)' }} />
        <div className="flex-1 ml-2 px-2 py-1 rounded text-[9px] font-mono truncate" style={{ background: 'var(--dash-hover-bg)', color: 'var(--dash-text-dim)' }}>
          checkout.bonespay.com/pay/preview
        </div>
      </div>

      <div className="relative overflow-hidden" style={{ height: 560 }}>
        <div style={{ transform: 'scale(0.65)', transformOrigin: 'top left', width: '153.8%' }}>
          <div
            className="relative flex items-start justify-center p-8 overflow-hidden"
            style={{ ...themeVars, ...bgStyle, minHeight: 860 } as React.CSSProperties}
          >
            {hasImageBg && (
              <div
                className="absolute inset-0 z-0"
                style={{
                  backgroundColor: isDark ? '#000000' : '#ffffff',
                  opacity: (branding.background_image_overlay ?? 40) / 100,
                }}
              />
            )}

            {assetInstances.map((inst) => (
              <img
                key={inst.idx}
                src={inst.asset.url}
                alt=""
                className="absolute pointer-events-none select-none z-[1]"
                style={{
                  width: `${inst.asset.size * 0.6}px`,
                  height: `${inst.asset.size * 0.6}px`,
                  left: `${inst.left}%`,
                  top: `${inst.top}%`,
                  opacity: inst.asset.opacity / 100,
                  objectFit: 'contain',
                  animation: `bp-pv-${inst.asset.animation} ${SPEED_MAP[inst.asset.speed] || '5s'} ease-in-out infinite`,
                  animationDelay: `${inst.delay}s`,
                }}
              />
            ))}

            <div className="relative z-10 w-full" style={{ maxWidth: 420 }}>
              <div className={`mb-6 ${centered ? 'text-center' : 'text-left'}`}>
                {branding.logo_url ? (
                  <div className={centered ? 'flex justify-center' : ''}>
                    <img
                      src={branding.logo_url}
                      alt={merchantName}
                      style={{ height: `${logoHeight * 0.7}px`, maxWidth: 210, objectFit: 'contain' }}
                      className="inline-block"
                    />
                  </div>
                ) : (
                  <p
                    className="text-sm font-medium tracking-wide"
                    style={{ color: 'var(--bp-text-secondary)', fontFamily: 'var(--bp-body-font)' }}
                  >
                    {branding.business_name || merchantName}
                  </p>
                )}
              </div>

              <div
                className="p-6"
                style={{
                  background: 'var(--bp-card-bg)',
                  border: '1px solid var(--bp-border)',
                  borderRadius: 'var(--bp-radius)',
                }}
              >
                <h2
                  className="text-lg font-semibold mb-1"
                  style={{ color: 'var(--bp-text)', fontFamily: 'var(--bp-heading-font)' }}
                >
                  {branding.checkout_title || 'Complete Your Purchase'}
                </h2>
                {branding.checkout_description && (
                  <p
                    className="text-sm mb-4"
                    style={{ color: 'var(--bp-text-secondary)', fontFamily: 'var(--bp-body-font)' }}
                  >
                    {branding.checkout_description}
                  </p>
                )}
                {!branding.checkout_description && <div className="mb-4" />}

                <div style={{ borderBottom: '1px solid var(--bp-border)' }} className="pb-3 mb-3">
                  <div className="flex items-center gap-3 mb-2">
                    <div
                      className="w-10 h-10 rounded flex-shrink-0"
                      style={{ background: 'var(--bp-border)', borderRadius: 'var(--bp-radius)' }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm" style={{ color: 'var(--bp-text)', fontFamily: 'var(--bp-body-font)' }}>
                        Premium Widget
                      </div>
                      <div className="text-xs" style={{ color: 'var(--bp-text-secondary)' }}>Qty: 2 x $19.99</div>
                    </div>
                    <div className="text-sm font-medium" style={{ color: 'var(--bp-text)', fontFamily: 'var(--bp-body-font)' }}>
                      $39.98
                    </div>
                  </div>
                  <div className="space-y-1 pt-2" style={{ borderTop: '1px solid var(--bp-border)' }}>
                    <div className="flex justify-between">
                      <span className="text-xs" style={{ color: 'var(--bp-text-secondary)' }}>Subtotal</span>
                      <span className="text-xs font-medium" style={{ color: 'var(--bp-text)' }}>$39.98</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs" style={{ color: 'var(--bp-text-secondary)' }}>Shipping</span>
                      <span className="text-xs font-medium" style={{ color: 'var(--bp-text)' }}>$5.00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs" style={{ color: 'var(--bp-success)' }}>Coupon: SAVE5</span>
                      <span className="text-xs font-medium" style={{ color: 'var(--bp-success)' }}>-$5.00</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs" style={{ color: 'var(--bp-text-secondary)' }}>Tax</span>
                      <span className="text-xs font-medium" style={{ color: 'var(--bp-text)' }}>$4.00</span>
                    </div>
                  </div>
                </div>

                {branding.show_order_summary !== false && (
                  <div style={{ borderBottom: '1px solid var(--bp-border)' }} className="pb-4 mb-5">
                    <div className="flex justify-between items-center">
                      <span
                        className="text-base font-semibold"
                        style={{ color: 'var(--bp-text)', fontFamily: 'var(--bp-heading-font)' }}
                      >
                        Total
                      </span>
                      <span
                        className="text-xl font-semibold"
                        style={{ color: 'var(--bp-text)', fontFamily: 'var(--bp-heading-font)' }}
                      >
                        $43.98 <span className="text-xs font-normal uppercase">USD</span>
                      </span>
                    </div>
                  </div>
                )}

                <label
                  className="block text-xs uppercase tracking-wider font-medium mb-2"
                  style={{ color: 'var(--bp-text-secondary)', fontFamily: 'var(--bp-body-font)' }}
                >
                  Card details
                </label>
                <div
                  className="px-3 py-3 mb-4 text-sm"
                  style={{
                    border: '1px solid var(--bp-border)',
                    borderRadius: 'var(--bp-radius)',
                    background: isDark ? 'rgba(0,0,0,0.2)' : '#f9f9fb',
                    color: 'var(--bp-text-secondary)',
                    fontFamily: 'var(--bp-body-font)',
                  }}
                >
                  4242 4242 4242 4242 &nbsp; 12/28 &nbsp; 123
                </div>

                <button
                  type="button"
                  className="w-full flex items-center justify-center gap-2 py-3 text-sm font-medium text-white"
                  style={{
                    background: 'var(--bp-accent)',
                    borderRadius: 'var(--bp-radius)',
                    fontFamily: 'var(--bp-body-font)',
                  }}
                >
                  <Lock size={14} />
                  Pay $43.98 USD
                </button>
              </div>

              <div className="mt-5 space-y-3 text-center">
                {branding.show_secure_badge !== false && (
                  <div className="flex items-center justify-center gap-1.5">
                    <Lock size={12} style={{ color: 'var(--bp-text-secondary)' }} />
                    <span
                      className="text-[11px]"
                      style={{ color: 'var(--bp-text-secondary)', fontFamily: 'var(--bp-body-font)' }}
                    >
                      Secured with 256-bit SSL encryption
                    </span>
                  </div>
                )}
                {branding.checkout_footer_text && (
                  <p
                    className="text-[11px]"
                    style={{ color: 'var(--bp-text-secondary)', fontFamily: 'var(--bp-body-font)' }}
                  >
                    {branding.checkout_footer_text}
                  </p>
                )}
                {branding.show_powered_by !== false && (
                  <p
                    className="text-[10px] opacity-50"
                    style={{ color: 'var(--bp-text-secondary)', fontFamily: 'var(--bp-body-font)' }}
                  >
                    Powered by BonesPay
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
